import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { TimeRecord } from '@/types';
import { CalendarView, generateMonthDays, generateWeekDays, CalendarDay } from '@/lib/calendar';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay } from 'date-fns';

export function useCalendar(userId: string, initialView: CalendarView = 'month') {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>(initialView);
  const [records, setRecords] = useState<TimeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);

  // Firestoreからタイムレコードを取得
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // 表示期間に応じて取得範囲を調整
    let startDate: Date;
    let endDate: Date;

    switch (view) {
      case 'month':
        startDate = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 });
        endDate = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 });
        break;
      case 'week':
        startDate = startOfWeek(currentDate, { weekStartsOn: 0 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 0 });
        break;
      case 'day':
        startDate = startOfDay(currentDate);
        endDate = endOfDay(currentDate);
        break;
    }

    try {
      const recordsRef = collection(db, 'users', userId, 'timestamps');
      const recordsQuery = query(
        recordsRef,
        where('clockInTime', '>=', Timestamp.fromDate(startDate)),
        where('clockInTime', '<=', Timestamp.fromDate(endDate)),
        orderBy('clockInTime', 'desc')
      );

      const unsubscribe = onSnapshot(
        recordsQuery,
        (snapshot) => {
          const fetchedRecords: TimeRecord[] = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as TimeRecord[];
          setRecords(fetchedRecords);
          setLoading(false);
        },
        (error) => {
          console.error('Failed to fetch records:', error);
          setRecords([]);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (error) {
      console.error('Failed to setup listener:', error);
      setRecords([]);
      setLoading(false);
    }
  }, [userId, currentDate, view]);

  // カレンダー日付配列を生成
  useEffect(() => {
    if (view === 'month') {
      setCalendarDays(generateMonthDays(currentDate, records));
    } else if (view === 'week') {
      setCalendarDays(generateWeekDays(currentDate, records));
    } else {
      // dayビューの場合は単一日
      setCalendarDays(generateWeekDays(currentDate, records).filter(day => 
        day.date.toDateString() === currentDate.toDateString()
      ));
    }
  }, [currentDate, records, view]);

  return {
    currentDate,
    setCurrentDate,
    view,
    setView,
    records,
    loading,
    calendarDays,
  };
}
